// NGRX

// OLCI
import { OlciActionTypes } from '../actions';
import { CreditCard, AccountCard, GuestsPaymentAccount, SelectedCard } from '../models/guest-oexpenses';
import { constants } from '../constants/constants';
import { PaymentType } from '../models/guest-oexpenses-payment-type';
export interface State {
  loading: boolean;
  bookingId: string | any;
  displayCCForm: { [guestId: string]: string };
  displayCCList: { [guestId: string]: boolean };
  error: any;
  accountCards: AccountCard[] | any;
  guestsPaymentAccounts: GuestsPaymentAccount[] | any;
  selectedCard: SelectedCard | null;
}

export const initialState: State = {
  loading: false,
  bookingId: null,
  displayCCForm: {},
  displayCCList: {},
  error: null,
  accountCards: null,
  guestsPaymentAccounts: null,
  selectedCard: null
};

export function reducer(state: State = initialState, action: any): State {
  let dccf: { [guestId: string]: string; } | null = null,
      dccl: { [guestId: string]: boolean; } | null = null;

  switch (action.type) {
    case OlciActionTypes.RESET_GUESTS_ONBOARD_EXPENSES_ERROR:
      return { ...state, error: null };

    case OlciActionTypes.RESET_DISPLAY_CC_FORM:
      return { ...state, displayCCForm: {} };

    case OlciActionTypes.RESET_DISPLAY_CC_LIST:
      return { ...state, displayCCList: {} };

    case OlciActionTypes.SET_DISPLAY_CC_FORM:
      dccf = { ...state.displayCCForm };
      dccf[action.payload.guestId] = action.payload.mode;
      return { ...state, displayCCForm: dccf };

    case OlciActionTypes.SET_DISPLAY_CC_LIST:
      dccl = { ...state.displayCCList };
      dccl[action.payload.guestId] = action.payload.visible;
      return { ...state, displayCCList: dccl };

    case OlciActionTypes.LOAD_GUESTS_ONBOARD_EXPENSES:
    case OlciActionTypes.POST_ONBOARD_EXPENSE:
      return { ...state, loading: true, error: null };

    case OlciActionTypes.POST_ONBOARD_EXPENSE_SELECTED_CC:
      return { ...state, selectedCard: action.payload };

    case OlciActionTypes.POST_ONBOARD_EXPENSE_SUCCESS:
      if (action.payload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
        return { 
          ...state,
          loading: false,
          guestsPaymentAccounts: [...state.guestsPaymentAccounts],
          error: action.payload.payload.errors
        };
      }
      return postOnBoardExpenseErrorReducer(action, state);
    case OlciActionTypes.LOAD_GUESTS_ONBOARD_EXPENSES_SUCCESS:
      if (action.payload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
        return { ...state, loading: false, error: action.payload.status };
      }
      return { 
        ...state,
        loading: false,
        error: null,
        bookingId: action.payload.bookingId,
        accountCards: action.payload.accountCards,
        guestsPaymentAccounts: action.payload.guestsPaymentAccounts
      };

    case OlciActionTypes.RESET_GUESTS_ONBOARD_EXPENSES:
      return { ...initialState };

    default:
      return state;
  }
}

export const getGuestsExpensesIsLoading = (state: State) => state.loading;
export const getGuestsExpensesInformation = (state: State) => state.guestsPaymentAccounts;
export const getGuestsExpensesError = (state: State) => state.error;
export const getDisplayCCForm = (state: State) => state.displayCCForm;
export const getDisplayCCList = (state: State) => state.displayCCList;
export const getSelectedCard = (state: State) => state.selectedCard;

function postOnBoardExpenseErrorReducer(action: any, state: State) : State {
  let guestsExpensesUpdated: GuestsPaymentAccount[] = [];
  let dccf: { [guestId: string]: string } = {};
  let dccl: { [guestId: string]: boolean } = {};

  for (const paymentAccounts of action.payload.payload.paymentAccounts) {
    const paymentType = getPaymentType(paymentAccounts);
    const creditCard: CreditCard | any = getCreditCard(paymentType,paymentAccounts, action);
    const onboardExpensesAffected: GuestsPaymentAccount[] = [];

    dccl = { ...state.displayCCList };

    for (const grantee of paymentAccounts.grantees) {
      const granteeList = paymentAccounts.grantees.map((granteeAccount: any) => ({
        guestId: granteeAccount.guestReservationId.guestId
      }));
      const onboardExpense: GuestsPaymentAccount = {
        guestId: grantee.guestReservationId.guestId,
        expensesAccount: {
          accountId: paymentAccounts.account.accountId.toString(),
          paymentType,
          creditCard,
          consentContent: null,
          bookingAccessToken: null,
          granteeList: grantee.guestReservationId.guestId === paymentAccounts.grantor.guestReservationId.guestId ? granteeList : []
        }
      };
      onboardExpensesAffected.push(onboardExpense);
      if (dccl[grantee.guestReservationId.guestId.toString()]) {
        dccl[grantee.guestReservationId.guestId.toString()] = false;
      }
    }

    const guestsAffectedIds = onboardExpensesAffected.map(onboardExpense => onboardExpense.guestId);
    guestsExpensesUpdated = state.guestsPaymentAccounts.filter((guest: any) => !guestsAffectedIds.includes(guest.guestId));
    guestsExpensesUpdated.push(...onboardExpensesAffected);

    dccf = { ...state.displayCCForm };
    dccf[paymentAccounts.guestReservationId.guestId.toString()] = '';
  }

  return { 
    ...state,
    loading: false,
    guestsPaymentAccounts: guestsExpensesUpdated,
    displayCCForm: dccf,
    displayCCList: dccl,
    accountCards: action.payload.payload.accountCards
  };
}

function getPaymentType(paymentAccounts: any) {
  switch (paymentAccounts.account.paymentTypeCode) {
    case constants.PAYMENT_TYPE.CASH:
      return PaymentType.Cash;
    case constants.PAYMENT_TYPE.NO_CHARGING_PRIVILEGES:
      return PaymentType.None;
    default:
      return PaymentType.CreditCard;
  }
}

function getCreditCard(paymentType: any, paymentAccounts: any, action: any): CreditCard | any {
  if (paymentType === PaymentType.CreditCard && paymentAccounts.account?.card) {
    return {
      cardType: paymentAccounts.account.paymentTypeCode,
      partialNumber: paymentAccounts.account.card.partialNumber,
      conversionAffirmationFlag: paymentAccounts.account.card.currencyConversion
        ? action.payload.payload.paymentAccounts[0].account.card.currencyConversion.conversionAffirmation
        : false,
      nonUSCardFlag: paymentAccounts.account.card.currencyConversion
        ? action.payload.payload.paymentAccounts[0].account.card.currencyConversion.eligibilityFlag
        : false,
      holderName: paymentAccounts.account.card.holderName,
      expirationMonth: paymentAccounts.account.card.expirationMonth,
      expirationYear: paymentAccounts.account.card.expirationYear,
      tokenText: paymentAccounts.account.card.tokenText
    }
  }
  return null;
}
